import { z } from "zod";
import { optionalTrimmedString, requiredString, trimmedString } from "./primitives";

export const orgRoleSchema = z.enum(["owner", "admin", "member"]);
export const orgMembershipStatusSchema = z.enum(["active", "invited", "inactive"]);
export const orgInviteStatusSchema = z.enum(["pending", "accepted", "revoked", "expired"]);

const emailString = z.preprocess(
  (value) => (typeof value === "string" ? value.trim() : value),
  z.string().email("Email must be valid.")
);

export const createOrgSchema = z.object({
  name: requiredString("Org name"),
});

export const orgSummarySchema = z.object({
  id: trimmedString,
  name: requiredString("Org name"),
});

export const orgMembershipSchema = z.object({
  id: trimmedString,
  orgId: trimmedString,
  userId: trimmedString,
  role: orgRoleSchema,
  status: orgMembershipStatusSchema,
});

export const orgInviteSchema = z.object({
  id: trimmedString,
  orgId: trimmedString,
  email: emailString,
  role: orgRoleSchema,
  status: orgInviteStatusSchema,
  expiresAt: trimmedString,
  acceptedAt: optionalTrimmedString,
  revokedAt: optionalTrimmedString,
  createdAt: trimmedString,
  updatedAt: trimmedString,
});

export const orgInviteWithTokenSchema = orgInviteSchema.extend({
  token: trimmedString,
});

export const orgInviteListResponseSchema = z.object({ invites: z.array(orgInviteSchema) });
export const orgInviteCreateResponseSchema = z.object({
  invite: orgInviteWithTokenSchema,
  acceptUrl: z.string().url().optional(),
  emailStatus: z.enum(["sent", "failed"]),
  message: z.string().optional(),
});
export const orgInviteRevokeResponseSchema = z.object({ invite: orgInviteSchema });
export const orgInviteAcceptResponseSchema = z.object({ org: orgSummarySchema, membership: orgMembershipSchema });

export const createOrgInviteSchema = z.object({
  email: emailString,
  role: orgRoleSchema,
});

export const acceptOrgInviteSchema = z.object({
  token: requiredString("Invite token"),
});

export const setActiveOrgSchema = z.object({
  orgId: requiredString("Org id"),
});

export type OrgRole = z.infer<typeof orgRoleSchema>;
export type OrgMembershipStatus = z.infer<typeof orgMembershipStatusSchema>;
export type OrgInviteStatus = z.infer<typeof orgInviteStatusSchema>;
export type CreateOrgPayload = z.infer<typeof createOrgSchema>;
export type OrgSummary = z.infer<typeof orgSummarySchema>;
export type OrgMembership = z.infer<typeof orgMembershipSchema>;
export type OrgInvite = z.infer<typeof orgInviteSchema>;
export type OrgInviteWithToken = z.infer<typeof orgInviteWithTokenSchema>;
export type CreateOrgInvitePayload = z.infer<typeof createOrgInviteSchema>;
export type AcceptOrgInvitePayload = z.infer<typeof acceptOrgInviteSchema>;
export type SetActiveOrgPayload = z.infer<typeof setActiveOrgSchema>;

export type OrgInviteCreateResponse = z.infer<typeof orgInviteCreateResponseSchema>;
